import '@/i18n'; // Initialize i18n — must be before any component using useTranslation
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/lib/auth';
import { PresenceHeartbeat } from '@/components/presence-heartbeat';
import { ErrorBoundary } from '@/components/features/error';
import { AuthGuard } from '@/components/features/auth';
import { AppShell } from '@/components/layout';
import { TooltipProvider } from '@/components/ui';
import { Toaster } from '@/components/ui/sonner';
import { ROLE_GROUPS } from '@/types/auth';
import type { UserRole } from '@/types/auth';
import {
  DashboardPage,
  LoginPage,
  ForgotPasswordPage,
  ChangePasswordPage,
  EmergencyLoginPage,
  MfaVerifyPage,
  MfaSetupPage,
  NotFoundPage,
  WebhooksPage,
  WebhookDetailPage,
  TransactionsPage,
  TransactionTriggerPage,
  TransactionDetailPage,
  SubscriptionsPage,
  ServicesPage,
  ServiceDetailPage,
  RoutingPage,
  ThemePage,
  PaymentLinkServicePage,
  IQServicesPage,
  IQServiceCreatePage,
  IQServiceDetailPage,
  WebhookConfigPage,
  MerchantsPage,
  MerchantDetailPage,
  MerchantAssetsPage,
  MerchantIqServicesPage,
  PlatformIqInventoryPage,
  AuditPage,
  AssetsPage,
  MerchantCreatePage,
  UsersPage,
  UserCreatePage,
  UserDetailPage,
  MnoIpRangesPage,
  MnoIpRangeCreatePage,
  MnoIpRangeEditPage,
  NetworkCoveragePage,
  ReportsPage,
  PaymentLinkPage,
  PaymentLinkTesterPage,
  DevportalRoutes,
} from '@/pages';
// Devportal-Track Mount-Point (Spec 247 §5): einziger App.tsx-Eingriff des Tracks
import { DEVPORTAL_ROLES, isDevportalEnabled } from '@/types/devportal';

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Protected route wrapper that includes AppShell and AuthGuard.
 */
function ProtectedRoute({
  children,
  requiredRoles,
}: {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}) {
  return (
    <AuthGuard requiredRoles={requiredRoles}>
      <AppShell>{children}</AppShell>
    </AuthGuard>
  );
}

/**
 * Main application component.
 * Spec 0043: Callback-Route entfernt, Login/Forgot/Change-Password hinzugefuegt.
 */
export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              {/* Spec 525: presence heartbeat — mounted above Routes so it
                  survives navigation; the hook itself gates on auth + visibility. */}
              <PresenceHeartbeat />
              <Routes>
                {/* Public routes — kein AuthGuard */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/change-password" element={<ChangePasswordPage />} />
                {/* MFA routes — Spec 0045 */}
                <Route path="/mfa-verify" element={<MfaVerifyPage />} />
                <Route path="/mfa-setup" element={
                  <AuthGuard>
                    <MfaSetupPage />
                  </AuthGuard>
                } />
                {/* Emergency access — Spec 0044 (kein Link auf normaler Login-Seite) */}
                <Route path="/auth/emergency" element={<EmergencyLoginPage />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/merchants"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.PLATFORM}>
                      <MerchantsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/merchants/new"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.MERCHANT_CRUD}>
                      <MerchantCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/merchants/:id"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.PLATFORM}>
                      <MerchantDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Merchant-Asset-Bibliothek (Spec 514/332, Contract v2.14) — echte BFF-Daten */}
                <Route
                  path="/merchants/:id/assets"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.PLATFORM}>
                      <MerchantAssetsPage />
                    </ProtectedRoute>
                  }
                />

                {/* IQ-Service-Inventar pro Merchant (Spec 344, UC-1) — platform-only */}
                <Route
                  path="/merchants/:id/iq-services"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.IQ_SERVICE_INVENTORY_ACCESS}>
                      <MerchantIqServicesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Issue #334: globaler Asset-Bibliothek-Einstieg (Configuration → Assets).
                    READ fuer alle Rollen; Merchant-Rollen sehen nur den eigenen Merchant. */}
                <Route
                  path="/assets"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.MERCHANT_ASSET_READ}>
                      <AssetsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.WEBHOOK_EVENTS_READ}>
                      <WebhooksPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks/config"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.WEBHOOK_CONFIG_ACCESS}>
                      <WebhookConfigPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks/:id"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.WEBHOOK_EVENTS_READ}>
                      <WebhookDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transactions"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <TransactionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transactions/trigger"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.TX_TRIGGER}>
                      <TransactionTriggerPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transactions/:transactionId"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <TransactionDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 429 Increment D: Analytics-Reports (QuickSight-Embed) */}
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.REPORTS_READ}>
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 493: Payment-Link Verwaltung — platform-only, read-only (Inc 1) */}
                <Route
                  path="/payment-link"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.PAYMENT_LINK_READ}>
                      <PaymentLinkPage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 501: Payment-Link Link-Tester — v1-Mount (platform-only wie die Flaeche) */}
                <Route
                  path="/payment-link/tester"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.PAYMENT_LINK_READ}>
                      <PaymentLinkTesterPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/subscriptions"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <SubscriptionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/services"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <ServicesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/services/:id"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <ServiceDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/services/:id/routing"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <RoutingPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/services/:id/theme"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.ALL}>
                      <ThemePage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 505/510: Payment-Link Service-Wizard — seit Spec 510 auch fuer
                    PM + merchant_it (:own, Tenant-Grenze erzwingt der BFF serverseitig);
                    platform_ops/merchant_viewer bleiben draussen (Sackgassen-Vermeidung). */}
                <Route
                  path="/services/:id/payment-link"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.PAYMENT_LINK_SERVICE_ACCESS}>
                      <PaymentLinkServicePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/iq-services"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.IQ_SERVICES_ACCESS}>
                      <IQServicesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 347 §8: Cross-Merchant-Uebersicht (platform-only, eigener flacher Pfad) */}
                <Route
                  path="/iq-service-inventory"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.IQ_SERVICE_INVENTORY_OVERVIEW}>
                      <PlatformIqInventoryPage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 528 Phase 6: Audit-Trail-Plattform-Sicht (platform_admin + platform_ops) */}
                <Route
                  path="/audit"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.AUDIT_READ}>
                      <AuditPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/iq-services/new"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.IQ_SERVICES_CRUD}>
                      <IQServiceCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/iq-services/:id"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.IQ_SERVICES_ACCESS}>
                      <IQServiceDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Spec 525: LIST is viewable by platform_ops (list-only, read);
                    /users/new + /users/:sub stay on USER_MANAGEMENT below. */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.USER_LIST_VIEW}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users/new"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.USER_MANAGEMENT}>
                      <UserCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users/:sub"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.USER_MANAGEMENT}>
                      <UserDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Issue #318: MNO-IP-Ranges Scaffold. Platform-Verwaltung
                    (CRUD nur platform_admin, platform_it read) + PM-Lese-Ansicht. */}
                <Route
                  path="/mno-ip-ranges"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.MNO_IP_RANGES_ACCESS}>
                      <MnoIpRangesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/mno-ip-ranges/new"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.MNO_IP_RANGES_CRUD}>
                      <MnoIpRangeCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/mno-ip-ranges/:operatorId/:countryCode"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.MNO_IP_RANGES_CRUD}>
                      <MnoIpRangeEditPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/network-coverage"
                  element={
                    <ProtectedRoute requiredRoles={ROLE_GROUPS.MNO_IP_RANGES_READ}>
                      <NetworkCoveragePage />
                    </ProtectedRoute>
                  }
                />

                {/* Devportal-Track Mount-Point (Spec 247 §5): lazy-loaded
                    /developer-Routen hinter Flag. Flag aus → Route nicht
                    registriert → Catch-all/404 (AC-1); Rollen lt. §4.1. */}
                {isDevportalEnabled() && (
                  <Route
                    path="/developer/*"
                    element={
                      <ProtectedRoute requiredRoles={DEVPORTAL_ROLES}>
                        <DevportalRoutes />
                      </ProtectedRoute>
                    }
                  />
                )}

                {/* 404 catch-all */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </TooltipProvider>
            <Toaster />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
